import { useApolloClient, useMutation } from "@apollo/client";
import { Dropdown } from "pepsico-ds";
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { deleteKeywordGroup } from "../../graphql/mutations/deleteKeywordGroup";
import { updateTag } from "../../graphql/mutations/renameStoreGroup";
import { updateKeywordGroupCategories } from "../../graphql/mutations/updateKeywordGroupCategories";
import { getOutletKeywordGroups } from "../../graphql/queries/getOutletKeywordGroups";
import { useStoreGroupsSummary } from "../../hooks/useStoreGroupsSummary";
import { useStores } from "../../hooks/useStores";
import { useToast } from "../../hooks/useToast";
import RenameStoreGroupModal from "./RenameStoreGroupModal";
import StoreFileUploadModal from "./StoreFileUploadModal";
import StoreGroupModal from "./StoreGroupModal";
import StoreGroupsListView from "./StoreGroupsListView";
import StoresListView from "./StoresListView";
import { VIEW_OPTIONS } from "./storeConstants";
import "./storeManagement.scss";

export default function StoreManagementPage() {
  const navigate = useNavigate();
  const { stores, allStores, loading, searchTerm, filterData, handlers, refetch } =
    useStores();
  const {
    storeGroups,
    loading: storeGroupsLoading,
    refetch: refetchStoreGroups,
  } = useStoreGroupsSummary();

  const client = useApolloClient();
  const { refetchProgramConfig } = useContext(GlobalConfigContext);
  const { toast, showToast, closeToast } = useToast();

  const [executeUpdateCategories] = useMutation(updateKeywordGroupCategories);
  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);
  const [executeRename] = useMutation(updateTag);
  const [isRenaming, setIsRenaming] = useState(false);

  const [activeView, setActiveView] = useState([VIEW_OPTIONS[0]]);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [targetGroup, setTargetGroup] = useState(null);

  const currentView = activeView?.[0]?.id || "stores";

  const handleViewChange = (selected) => {
    setActiveView(selected);
    const viewId = selected?.[0]?.id;
    if (viewId === "store-groups") {
      refetchStoreGroups();
    }
  };

  // Navigate to create store page
  const handleAddSingleStore = () => {
    navigate("/store-management/create");
  };

  // Open import file modal
  const handleImportFile = () => {
    setShowFileUploadModal(true);
  };

  // Navigate to edit store page
  const handleEditStore = (store) => {
    navigate(`/store-management/edit/${store.id}`);
  };

  // Delete a single store
  const handleDeleteStore = async (id, name) => {
    try {
      await executeDeleteKeyword({ variables: { id } });
      showToast("success", "Store Deleted", `"${name}" has been deleted`);
      if (refetch) refetch();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete the store"
      );
    }
  };

  // Bulk delete
  const handleDeleteMultiple = async (storeIds) => {
    try {
      await Promise.all(
        storeIds.map((storeId) =>
          executeDeleteKeyword({ variables: { id: storeId } })
        )
      );
      showToast("success", "Stores Deleted", `${storeIds.length} stores deleted`);
      if (refetch) refetch();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete stores"
      );
    }
  };

  const handleFileUploadSuccess = () => {
    showToast("success", "Import Completed", "Store data imported successfully");
    if (refetch) refetch();
    refetchStoreGroups();
  };

  // Store Group handlers
  const handleAddGroup = () => {
    setShowGroupModal(true);
  };

  const handleEditGroup = useCallback((groupName) => {
    setTargetGroup(groupName);
    setRenameModalOpen(true);
  }, []);

  const handleRenameClose = () => {
    setRenameModalOpen(false);
    setTargetGroup(null);
  };

  const handleRenameConfirm = async (newName) => {
    if (!newName || newName === targetGroup) {
      return;
    }
    setIsRenaming(true);
    try {
      await executeRename({
        variables: { oldName: targetGroup, newName, type: "STORE_GROUPS" },
      });

      const { data } = await client.query({
        query: getOutletKeywordGroups,
        variables: { filters: { category: targetGroup } },
        fetchPolicy: "network-only",
      });

      const storeIds = (data?.listKeywordGroups || []).map((s) => s.id);
      if (storeIds.length > 0) {
        await executeUpdateCategories({
          variables: {
            input: { keywordGroupIds: storeIds, type: "OUTLET", category: newName },
          },
        });
      }

      showToast(
        "success",
        "Group Renamed",
        `"${targetGroup}" has been renamed to "${newName}"`
      );
      if (refetchProgramConfig) refetchProgramConfig();
      if (refetchStoreGroups) refetchStoreGroups();
      handleRenameClose();
    } catch (error) {
      showToast(
        "error",
        "Rename Failed",
        error.message || "Could not rename the store group"
      );
    } finally {
      setIsRenaming(false);
    }
  };

  const handleViewGroup = useCallback(
    (groupName) => {
      handlers.handleFilterChange({ ...filterData, category: groupName });
      setActiveView([VIEW_OPTIONS[0]]);
    },
    [handlers, filterData]
  );

  const handleSaveGroup = async (groupName, storeIds) => {
    try {
      await executeUpdateCategories({
        variables: {
          input: {
            keywordGroupIds: storeIds,
            type: "OUTLET",
            category: groupName,
          },
        },
      });

      showToast(
        "success",
        "Store Group Saved",
        `Stores have been added to group "${groupName}"`
      );

      if (refetch) refetch();
      refetchStoreGroups();
    } catch (error) {
      showToast("error", "Error", error.message || "Failed to update store group");
    }
  };

  return (
    <div className="container">
      <div className="wrap">
        <div className="store-management-container">
          <div className="page-header">
            <h3 className="page-title">
              Available {currentView === "stores" ? "Stores" : "Store Groups"}
            </h3>
            <div className="page-header-actions">
              <div className="view-switcher-inline">
                <Dropdown
                  childList={VIEW_OPTIONS}
                  selection="single"
                  selectedValue={activeView}
                  placeholder="Select View"
                  size="medium"
                  setSelectedValue={handleViewChange}
                />
              </div>
            </div>
          </div>

          {currentView === "stores" ? (
            <StoresListView
              stores={stores}
              allStores={allStores}
              storeGroups={storeGroups}
              loading={loading}
              searchTerm={searchTerm}
              filterData={filterData}
              handlers={handlers}
              onAddSingleStore={handleAddSingleStore}
              onImportFile={handleImportFile}
              onEditStore={handleEditStore}
              onDeleteStore={handleDeleteStore}
              onDeleteMultiple={handleDeleteMultiple}
              onSaveGroup={handleSaveGroup}
            />
          ) : (
            <StoreGroupsListView
              storeGroups={storeGroups}
              loading={storeGroupsLoading}
              onViewGroup={handleViewGroup}
              onAddGroup={handleAddGroup}
              onEditGroup={handleEditGroup}
              showToast={showToast}
              refetchStoreGroups={refetchStoreGroups}
              refetchProgramConfig={refetchProgramConfig}
            />
          )}
        </div>

        <StoreFileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => setShowFileUploadModal(false)}
          onSuccess={handleFileUploadSuccess}
        />
        <StoreGroupModal
          isOpen={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          onSave={handleSaveGroup}
          onEditGroup={handleEditGroup}
          onGroupCreated={refetchStoreGroups}
          defaultMode={currentView === "store-groups" ? "new" : "existing"}
        />

        <RenameStoreGroupModal
          isOpen={renameModalOpen}
          targetGroup={targetGroup}
          onClose={handleRenameClose}
          onConfirm={handleRenameConfirm}
          loading={isRenaming}
        />

        <ToastNotification
          {...toast}
          handleClose={closeToast}
          sx={{ zIndex: 9999 }}
        />
      </div>
    </div>
  );
}
